/**
 * Pure recurrence-rule helpers for the Event edit modal (#4415).
 *
 * Extracted from EventEditModal.tsx so the modal's builder/classifier/parser for
 * RRULE strings live in one testable place (and to keep the modal under its
 * file-size ceiling). These are the SINGLE source of truth the live modal
 * delegates to: the Repeat-select classifier and the custom-control inverse-parse
 * both compare against exactly what `buildRrule` would re-emit, so classification
 * and re-emission can never diverge.
 *
 * NOT to be confused with the sibling `recurrence.ts`, which expands an rrule
 * into concrete occurrences for the calendar grid — a different concern.
 */
import { RRule } from 'rrule';

/**
 * Strip a leading "RRULE:" line prefix. Google Calendar sync stores the rule as
 * the wire form `RRULE:FREQ=...`; the in-modal builder/classifier works on the
 * bare `FREQ=...` body. Returns the trimmed body so preset matching and the
 * inverse-parse below see the same shape regardless of source (#4415).
 */
export function stripRrulePrefix(rrule: string): string {
  return rrule.replace(/^RRULE:/i, '').trim();
}

/** The named (non-custom, non-none) Repeat presets, in classification order. */
export const NAMED_PRESETS = ['daily', 'weekdays', 'weekly', 'monthly', 'yearly'] as const;
export type NamedPreset = (typeof NAMED_PRESETS)[number];

/**
 * Pure builder for a named preset's rrule body, given the event's start context.
 * This is the SINGLE source of truth the live `buildRrule` callback delegates to
 * for every non-custom/non-none branch, so the round-trip classifier below
 * compares against exactly what a save would re-emit (#4415).
 *
 * `startDate` is the modal's "YYYY-MM-DD" start; weekly anchors its BYDAY on the
 * start weekday and monthly its BYMONTHDAY on the start day-of-month — mirroring
 * the builder. An empty start falls back to today (the builder's own fallback).
 */
export function buildPresetRrule(preset: NamedPreset, startDate: string): string {
  if (preset === 'daily') return 'FREQ=DAILY';
  if (preset === 'weekdays') return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
  if (preset === 'weekly') {
    const d = startDate && startDate.trim() ? new Date(startDate + 'T00:00:00').getDay() : new Date().getDay();
    const dayStr = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][d];
    return `FREQ=WEEKLY;BYDAY=${dayStr}`;
  }
  if (preset === 'monthly') {
    const d = startDate && startDate.trim() ? new Date(startDate + 'T00:00:00').getDate() : new Date().getDate();
    return `FREQ=MONTHLY;BYMONTHDAY=${d}`;
  }
  // yearly
  return 'FREQ=YEARLY';
}

/**
 * Normalize an rrule body for the round-trip EXACT compare (#4415, review fix 4).
 *
 * Strips any leading "RRULE:" + surrounding whitespace, then UPPERCASES the body.
 * Uppercasing makes the classifier case-robust: RRule keys (and the day tokens
 * Google emits) are conventionally upper-case, but a lower/mixed-case body like
 * `freq=daily` must still classify to its preset, not fall to 'custom'. The
 * builder's own output is already canonical upper-case, so uppercasing both sides
 * of the compare is safe — every value the preset builder can emit (FREQ words,
 * BYDAY tokens MO..SU, integer BYMONTHDAY) is already upper-case/case-insensitive.
 *
 * This is for COMPARISON ONLY — never feed the result back to a save. The
 * original-cased string is preserved verbatim by the modal's dirty guard, so
 * an untouched rule round-trips byte-for-byte regardless of its source casing.
 */
export function normalizeRruleBody(rrule: string): string {
  return stripRrulePrefix(rrule).toUpperCase();
}

/**
 * Classify an rrule into a Repeat-preset value BY ROUND-TRIP (#4415).
 *
 * GUIDING INVARIANT: a rule maps to a named preset ONLY IF that preset's
 * `buildRrule(<preset>)` output reproduces it EXACTLY. So for each candidate
 * named preset we compute `buildPresetRrule(<preset>, startDate)` using the
 * event's own start context (the same inputs the builder uses) and compare its
 * normalized form to the normalized input rule. If exactly one preset matches,
 * use it; otherwise the rule is 'custom' (the only branch that re-emits
 * UNTIL/COUNT/INTERVAL>1, multi-day BYDAY, positional BYDAY, WKST, BYSETPOS, …).
 *
 * This single test subsumes the prior per-case heuristics: any parameter or
 * day-set the preset builder can't emit fails the exact compare and falls to
 * 'custom', so touching the Repeat select can never silently drop it (e.g.
 * bare FREQ=WEEKLY → builder adds BYDAY → not equal → custom; FREQ=WEEKLY;WKST=SU
 * → not equal → custom; FREQ=MONTHLY;BYMONTHDAY=N≠startDay → not equal → custom).
 * The compare is case-robust (normalizeRruleBody uppercases), so a lower-case
 * Google body classifies to the same preset its upper-case twin would.
 */
export function classifyRrulePreset(rrule: string, startDate: string): string {
  const normalized = normalizeRruleBody(rrule);
  const matches = NAMED_PRESETS.filter(
    (preset) => normalizeRruleBody(buildPresetRrule(preset, startDate)) === normalized,
  );
  return matches.length === 1 ? matches[0] : 'custom';
}

/**
 * Inverse of buildRrule's custom branch: parse an existing rrule body into the
 * custom-recurrence control state so opening an event seeds the Custom UI with
 * the event's actual rule instead of the WEEKLY/today defaults (#4415). Best
 * effort — the builder-overwrite guard is the hard identity-preservation
 * invariant; this only makes the controls display the right thing.
 *
 * RRule weekday convention is Mon=0..Sun=6; the modal's customDays uses JS
 * getDay() (Sun=0..Sat=6), so weekdays are mapped across.
 *
 * Returns null when the rule is unrepresentable by the custom controls, so the
 * caller treats it as unrepresentable-custom (the dirty guard preserves it
 * verbatim while untouched). In particular a POSITIONAL BYDAY like `BYDAY=1MO`
 * ('first Monday') carries an ordinal (`Weekday.n`) the day-button controls
 * cannot express; seeding `customDays=[MO]` would silently rebuild it as
 * `BYDAY=MO` ('every Monday') on the first control touch, changing the series
 * semantics. We bail to null instead (#4415, review fix 5).
 */
export interface CustomRecurrenceState {
  customFreq: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  customInterval: number;
  customDays: number[];
  customEndType: 'never' | 'count' | 'until';
  customCount: number;
  customUntil: string;
}

export function parseCustomRecurrence(body: string): CustomRecurrenceState | null {
  let parsed: ReturnType<typeof RRule.parseString>;
  try {
    parsed = RRule.parseString(body);
  } catch {
    return null;
  }
  const freqMap: Record<number, CustomRecurrenceState['customFreq']> = {
    [RRule.DAILY]: 'DAILY',
    [RRule.WEEKLY]: 'WEEKLY',
    [RRule.MONTHLY]: 'MONTHLY',
    [RRule.YEARLY]: 'YEARLY',
  };
  const customFreq = parsed.freq != null ? freqMap[parsed.freq] : undefined;
  if (!customFreq) return null;

  // RRule weekday (Mon=0..Sun=6) → JS getDay() (Sun=0..Sat=6).
  const toJsDay = (rruleWeekday: number) => (rruleWeekday + 1) % 7;
  let customDays: number[] = [];
  if (parsed.byweekday != null) {
    // RRule.parseString returns Weekday objects (never bare numbers) for BYDAY.
    // A positional BYDAY ('1MO', '-1FR') yields a Weekday with a non-null `.n`
    // ordinal the day-button controls can't represent — bail to null so the
    // rule stays verbatim-custom rather than being silently rebuilt as a plain
    // every-weekday BYDAY (#4415, review fix 5).
    const list = Array.isArray(parsed.byweekday) ? parsed.byweekday : [parsed.byweekday];
    const hasPositional = list.some((w: any) => w != null && w.n != null);
    if (hasPositional) return null;
    customDays = list
      .map((w: any) => w?.weekday)
      .filter((n: any): n is number => typeof n === 'number')
      .map(toJsDay)
      .sort((a, b) => a - b);
  }

  let customEndType: CustomRecurrenceState['customEndType'] = 'never';
  let customCount = 10;
  let customUntil = '';
  if (parsed.count != null) {
    customEndType = 'count';
    customCount = parsed.count;
  } else if (parsed.until instanceof Date && !isNaN(parsed.until.getTime())) {
    customEndType = 'until';
    // RRule.parseString returns `until` as a UTC Date (UNTIL=…Z). formatDate uses
    // local getters, so west of UTC it reads back the prior calendar day and the
    // rebuilt UNTIL truncates the series by a day. Read the UTC calendar date (#4415).
    const u = parsed.until;
    customUntil = `${u.getUTCFullYear()}-${String(u.getUTCMonth() + 1).padStart(2, '0')}-${String(u.getUTCDate()).padStart(2, '0')}`;
  }

  return {
    customFreq,
    customInterval: parsed.interval && parsed.interval > 0 ? parsed.interval : 1,
    customDays,
    customEndType,
    customCount,
    customUntil,
  };
}
